import React, { useMemo, useState } from "react";
import ContentWrapper from "../../components/layout/ContentWrapper";
import { useCustomTranslation } from "../../helpers/translateHelpers";
import {
  Button,
  Filter,
  FlexContent,
  PageHeading,
  SearchBox,
  useAuth,
} from "../../components";
import {
  CustomerPortalRequestTableQuery,
  OrderRequestStateArray,
  OrderRequestTypeArray,
} from "./models";
import RequestsTable from "./components/RequestsTable";
import { useNavigate } from "../../helpers/navigationHelpers";
import { SelectItem } from "../../components/select/Select";
import { getTableParsedFiltersForQuery } from "../../helpers/tableHelpers";

const defaultTableQuery: CustomerPortalRequestTableQuery = {
  page: 1,
  pageSize: 10,
};

const RequestsPage: React.FC = (): React.ReactElement => {
  const { user } = useAuth();
  const { tRequest, tCommon } = useCustomTranslation();
  const navigate = useNavigate();

  const [queryParams, setQueryParams] =
    useState<CustomerPortalRequestTableQuery>(defaultTableQuery);

  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, SelectItem[]>
  >({});

  const handleSearch = (searchPhrase: string) => {
    setQueryParams({ ...queryParams, searchPhrase });
  };

  const handleFilter = (selectedFilters: Record<string, SelectItem[]>) => {
    const parsedFilters = getTableParsedFiltersForQuery(selectedFilters);

    setQueryParams({
      searchPhrase: queryParams.searchPhrase,
      ...defaultTableQuery,
      ...parsedFilters,
    });
    setSelectedFilters(selectedFilters);
  };

  const requestTypesArray = useMemo(() => {
    if (!user?.ownsServiceObjects) {
      return OrderRequestTypeArray.filter(
        (type) => type !== "OrderRequestFromOwner",
      ).map((type) => ({
        label: tRequest(`type_${type}`),
        value: type,
      }));
    }

    return OrderRequestTypeArray.map((type) => ({
      label: tRequest(`type_${type}`),
      value: type,
    }));
  }, [tRequest, user?.ownsServiceObjects]);

  return (
    <ContentWrapper htmlTitle={tCommon("menu_requests")}>
      <div className="mb-4 flex items-center justify-between">
        <PageHeading title={tRequest("table_heading")} />
        <FlexContent className="gap-4">
          <Filter
            selectedFitlers={selectedFilters}
            updateFilters={handleFilter}
            filters={[
              {
                key: "state",
                label: tRequest("state"),
                options: OrderRequestStateArray.map((state) => ({
                  value: state,
                  label: tRequest(`state_${state}`),
                })),
              },
              {
                key: "type",
                label: tRequest("type"),
                options: requestTypesArray,
              },
            ]}
          />
          <SearchBox onSearch={handleSearch} />
          <Button
            label={tRequest("create_button")}
            mode="primary"
            size="small"
            onClick={() => navigate.goTo("requests/create")}
          />
        </FlexContent>
      </div>
      <RequestsTable
        queryParams={queryParams}
        setQueryParams={setQueryParams}
      />
    </ContentWrapper>
  );
};

export default RequestsPage;
